/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState } from 'react';
import { Button } from '../primitives/button';
import { Input } from '../primitives/input';

interface PaginatorProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    minimal?: boolean;
}

const Paginator: React.FC<PaginatorProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    minimal = false,
}) => {
    const [editingPage, setEditingPage] = useState<'start' | 'end' | undefined>(
        undefined,
    );
    const [editingPageValue, setEditingPageValue] = useState<number | string>(
        1,
    );

    const handlePageChange = (page: number | string) => {
        const numericPage =
            typeof page === 'string' ? parseInt(page, 10) : page;
        if (
            !isNaN(numericPage) &&
            numericPage >= 1 &&
            numericPage <= totalPages
        ) {
            onPageChange(numericPage);
        }
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const startPage = Math.max(1, currentPage - 1);
        const endPage = Math.min(totalPages, currentPage + 1);

        const visiblePages = Array.from(
            { length: endPage - startPage + 1 },
            (_, index) => startPage + index,
        );

        if (currentPage > 2) {
            buttons.push(
                <Button
                    key="first"
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(1)}
                >
                    1
                </Button>,
            );
            if (currentPage > 3) {
                buttons.push(
                    editingPage !== 'start' ? (
                        <Button
                            key="start-ellipsis"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setEditingPageValue(currentPage - 1);
                                setEditingPage('start');
                            }}
                        >
                            ...
                        </Button>
                    ) : (
                        <Input
                            key="start-ellipsis"
                            className="w-16"
                            value={editingPageValue}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^[0-9]+$/.test(value)) {
                                    setEditingPageValue(
                                        value === '' ? '' : parseInt(value, 10),
                                    );
                                }
                            }}
                            onKeyUp={(e) => {
                                if (e.key === 'Enter') {
                                    handlePageChange(editingPageValue);
                                    setEditingPage(undefined);
                                }
                            }}
                            onBlur={() => {
                                handlePageChange(editingPageValue);
                                setEditingPage(undefined);
                            }}
                            autoFocus
                        />
                    ),
                );
            }
        }

        visiblePages.forEach((page) => {
            buttons.push(
                <Button
                    key={page}
                    variant={page === currentPage ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Button>,
            );
        });

        if (currentPage < totalPages - 1) {
            if (currentPage < totalPages - 2) {
                buttons.push(
                    editingPage !== 'end' ? (
                        <Button
                            key="end-ellipsis"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setEditingPageValue(currentPage + 1);
                                setEditingPage('end');
                            }}
                        >
                            ...
                        </Button>
                    ) : (
                        <Input
                            key="end-ellipsis"
                            className="w-16"
                            value={editingPageValue}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || /^[0-9]+$/.test(value)) {
                                    setEditingPageValue(
                                        value === '' ? '' : parseInt(value, 10),
                                    );
                                }
                            }}
                            onKeyUp={(e) => {
                                if (e.key === 'Enter') {
                                    handlePageChange(editingPageValue);
                                    setEditingPage(undefined);
                                }
                            }}
                            onBlur={() => {
                                handlePageChange(editingPageValue);
                                setEditingPage(undefined);
                            }}
                            autoFocus
                        />
                    ),
                );
            }
            buttons.push(
                <Button
                    key="last"
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </Button>,
            );
        }

        return buttons;
    };

    return (
        <div className="flex items-center justify-center space-x-2">
            <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
            >
                Previous
            </Button>
            {!minimal && renderPaginationButtons()}
            <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
            >
                Next
            </Button>
        </div>
    );
};

export default Paginator;
